import React, { FC } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { Park, Layers, Description, FolderZip } from '@mui/icons-material';
import { Site } from 'src/api/Api';
import { ProjectWithSites } from 'src/state-management/state';

interface LeftPanelProps {
  projectDetails: ProjectWithSites;
  sites: Site[];
  isLoadingSites: boolean;
  activeLeftTab: 'layers' | 'reports' | 'dataroom';
  setActiveLeftTab: (tab: 'layers' | 'reports' | 'dataroom') => void;
  selectedSite: Site | null;
  onSiteClick: (site: Site) => void;
  onReadMoreClick: () => void;
  onNewSessionClick: () => void;
  width?: number;
}

const LeftPanel: FC<LeftPanelProps> = ({
  projectDetails,
  sites,
  isLoadingSites,
  activeLeftTab,
  setActiveLeftTab,
  selectedSite,
  onSiteClick,
  onReadMoreClick,
  onNewSessionClick,
  width,
}) => {
  const isDataRoomEnabled = Boolean(projectDetails?.is_data_room_enabled);

  return (
    <Box className="left-panel glass-panel" style={{ width: width !== undefined ? width : undefined, minWidth: width !== undefined ? width : undefined, maxWidth: width !== undefined ? width : undefined }}>
      <Box className="left-panel-content">
        <Typography className="overview-title">
        <Park /> Project Overview
      </Typography>
      <Typography className="overview-description">
        {projectDetails.project_description ||
          `Current focus: ${projectDetails.project_name} Analysis. Integrating multi-spectral satellite imagery with ground-level bioacoustic sensors to measure species return rates in degraded primary forest zones.`}
      </Typography>
      <Box className="project-action-row">
        <Button variant="contained" className="read-more-btn" onClick={onReadMoreClick} sx={{ width: '100%' }}>
          Read More
        </Button>
      </Box>

      {/* Navigation vertical buttons */}
      <Box className="nav-tabs-vertical">
        <Button
          className={`tab-item-btn ${activeLeftTab === 'layers' ? 'active' : ''}`}
          startIcon={<Layers />}
          onClick={() => setActiveLeftTab('layers')}
        >
          Layers
        </Button>
        <Button
          className={`tab-item-btn ${activeLeftTab === 'reports' ? 'active' : ''}`}
          startIcon={<Description />}
          onClick={() => setActiveLeftTab('reports')}
        >
          Reports
        </Button>
        {isDataRoomEnabled && (
          <Button
            className={`tab-item-btn ${activeLeftTab === 'dataroom' ? 'active' : ''}`}
            startIcon={<FolderZip />}
            onClick={() => setActiveLeftTab('dataroom')}
          >
            Data Room
          </Button>
        )}
      </Box>

      {/* Status tracking list */}
      <Box className="sites-status-section">
        <Typography className="section-heading-small">
          {activeLeftTab === 'layers' ? 'Active Sites Status' : 'Reports Log'}
        </Typography>

        {isLoadingSites ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={20} />
          </Box>
        ) : activeLeftTab === 'layers' ? (
          <Box className="site-status-list">
            {sites.length === 0 ? (
              <Typography sx={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                No sites added yet.
              </Typography>
            ) : (
              sites.map((site) => {
                let statusText = 'Draft';
                let statusClass = 'draft';
                if (
                  site.env_analysis_status === 'Finished' ||
                  site.bio_analysis_status === 'Finished' ||
                  site.bio_analysis_status === 'COMPLETED'
                ) {
                  statusText = 'Completed';
                  statusClass = 'completed';
                } else if (
                  site.env_analysis_status === 'Pending' ||
                  site.bio_analysis_status === 'PENDING'
                ) {
                  statusText = 'Ongoing';
                  statusClass = 'ongoing';
                }

                return (
                  <Box
                    key={site.site_id}
                    className="site-status-item"
                    style={{
                      borderLeft:
                        selectedSite?.site_id === site.site_id
                          ? '2px solid #10b981'
                          : '2px solid transparent',
                      background:
                        selectedSite?.site_id === site.site_id
                          ? 'rgba(16, 185, 129, 0.08)'
                          : 'transparent',
                    }}
                    onClick={() => onSiteClick(site)}
                  >
                    <Box className="site-name-box">
                      <Box className={`status-indicator-dot ${statusClass}`} />
                      <span className="site-name-lbl">{site.site_name}</span>
                    </Box>
                    <span className={`status-text-lbl ${statusClass}`}>{statusText}</span>
                  </Box>
                );
              })
            )}
          </Box>
        ) : (
          <Box className="site-status-list">
            {projectDetails?.project_name?.toLowerCase().includes('tata motors') ? (
              <Box className="site-status-item" sx={{ cursor: 'default' }}>
                <Box className="site-name-box">
                  <Description sx={{ fontSize: 16, color: '#999' }} />
                  <span className="site-name-lbl"> Report</span>
                </Box>
                <span className="status-text-lbl completed">PDF</span>
              </Box>
            ) : (
              <Typography sx={{ fontSize: '12px', color: '#666', fontStyle: 'italic', padding: '16px' }}>
                No reports available for this project.
              </Typography>
            )}
          </Box>
        )}
      </Box>
      </Box>
    </Box>
  );
};

export default LeftPanel;
